const j = (r) => r.json();

// In dev the Vite proxy forwards /api → localhost:3002. In production set
// VITE_API_URL to the deployed API origin (e.g. https://dockdash-api.azurewebsites.net).
const API_BASE = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

// Attach the signed-in user's bearer token so the server can enforce permissions.
function authHeader() {
  try {
    const t = JSON.parse(localStorage.getItem('ll_session') || 'null')?.token;
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch { return {}; }
}
const f = async (path, opts = {}) => {
  const res = await fetch(API_BASE + path, { ...opts, headers: { ...(opts.headers || {}), ...authHeader() } });
  // 401 means the stored session is no longer valid (e.g. it predates bearer tokens, or
  // the server restarted). Clear it and go back to sign-in rather than letting error
  // payloads leak into component state. 403 is a permission denial — leave the user in.
  if (res.status === 401) {
    localStorage.removeItem('ll_session');
    location.reload();
    throw new Error('session expired');
  }
  return res;
};

// Endpoints that return a list must ALWAYS hand back an array. An error payload like
// {error:"..."} previously landed in state and blew up on .filter/.map.
const jList = (r) => r.json().then((d) => (Array.isArray(d) ? d : []));

export const api = {
  places: () => f('/api/places').then(jList),
  captains: () => f('/api/captains').then(jList),
  captain: (id) => f(`/api/captains/${id}`).then(j),
  registerCaptain: (body) =>
    f('/api/captains', { method: 'POST', headers: H, body: JSON.stringify(body) }).then(j),
  addReview: (id, body) =>
    f(`/api/captains/${id}/reviews`, { method: 'POST', headers: H, body: JSON.stringify(body) }).then(j),
  validatePoint: (body) =>
    f('/api/validate-point', { method: 'POST', headers: H, body: JSON.stringify(body) }).then(j),
  geocode: (q) =>
    f('/api/geocode', { method: 'POST', headers: H, body: JSON.stringify({ q }) }).then(j),
  geocodeSuggest: (q) =>
    f('/api/geocode-suggest', { method: 'POST', headers: H, body: JSON.stringify({ q }) }).then(j),
  verifyStart: (email) =>
    f('/api/verify-email/start', { method: 'POST', headers: H, body: JSON.stringify({ email }) }).then(j),
  verifyCheck: (email, code) =>
    f('/api/verify-email/check', { method: 'POST', headers: H, body: JSON.stringify({ email, code }) }).then(j),
  applications: () => f('/api/applications').then(jList),
  admins: () => f('/api/admins').then(j),
  addAdmin: (body) =>
    f('/api/admins', { method: 'POST', headers: H, body: JSON.stringify(body) }).then(j),
  updateAdmin: (id, body) =>
    f(`/api/admins/${id}`, { method: 'PUT', headers: H, body: JSON.stringify(body) }).then(j),
  removeAdmin: (id) => f(`/api/admins/${id}`, { method: 'DELETE' }).then(j),
  approveCaptain: (id) => f(`/api/captains/${id}/approve`, { method: 'POST' }).then(j),
  rejectCaptain: (id) => f(`/api/captains/${id}/reject`, { method: 'POST' }).then(j),
  notifications: () => f('/api/notifications').then(jList),
  markNotificationsRead: () => f('/api/notifications/read', { method: 'POST' }).then(j),
  requestCode: (identifier) =>
    f('/api/auth/request-code', { method: 'POST', headers: H, body: JSON.stringify({ identifier }) }).then(j),
  verifyCode: (identifier, code, name) =>
    f('/api/auth/verify-code', { method: 'POST', headers: H, body: JSON.stringify({ identifier, code, name }) }).then(j),
  feed: (kind, id) => f(`/api/notifications/feed?kind=${kind}&id=${encodeURIComponent(id)}`).then(jList),
  feedRead: (kind, id) => f('/api/notifications/feed/read', { method: 'POST', headers: H, body: JSON.stringify({ kind, id }) }).then(j),
  riders: () => f('/api/riders').then(jList),
  rider: (id) => f(`/api/riders/${id}`).then(j),
  addRiderReview: (id, body) =>
    f(`/api/riders/${id}/reviews`, { method: 'POST', headers: H, body: JSON.stringify(body) }).then(j),
  favorites: (id) => f(`/api/riders/${id}/favorites`).then(jList),
  addFavorite: (id, body) =>
    f(`/api/riders/${id}/favorites`, { method: 'POST', headers: H, body: JSON.stringify(body) }).then(j),
  removeFavorite: (fid) => f(`/api/favorites/${fid}`, { method: 'DELETE' }).then(j),
  fareRules: () => f('/api/fare-rules').then(j),
  saveFareRules: (body) =>
    f('/api/fare-rules', { method: 'PUT', headers: H, body: JSON.stringify(body) }).then(j),
  tiers: () => f('/api/pricing-tiers').then(jList),
  createTier: (body) =>
    f('/api/pricing-tiers', { method: 'POST', headers: H, body: JSON.stringify(body) }).then(j),
  updateTier: (id, body) =>
    f(`/api/pricing-tiers/${id}`, { method: 'PUT', headers: H, body: JSON.stringify(body) }).then(j),
  deleteTier: (id) => f(`/api/pricing-tiers/${id}`, { method: 'DELETE' }).then(j),
  setStatus: (id, body) =>
    f(`/api/captains/${id}/status`, { method: 'POST', headers: H, body: JSON.stringify(body) }).then(j),
  quote: (body) =>
    f('/api/quote', { method: 'POST', headers: H, body: JSON.stringify(body) }).then(j),
  route: (body) =>
    f('/api/route', { method: 'POST', headers: H, body: JSON.stringify(body) }).then(j),
  requestRide: (body) =>
    f('/api/rides', { method: 'POST', headers: H, body: JSON.stringify(body) }).then(j),
  ride: (id) => f(`/api/rides/${id}`).then(j),
  rides: () => f('/api/rides').then(jList),
  pay: (id) => f(`/api/rides/${id}/pay`, { method: 'POST' }).then(j),
  tip: (id, amount) => f(`/api/rides/${id}/tip`, { method: 'POST', headers: H, body: JSON.stringify({ amount }) }).then(j),
  paymentMode: () => f('/api/payment-mode').then(j),
  paymentConfirmed: (id) => f(`/api/rides/${id}/payment-confirmed`, { method: 'POST' }).then(j),
  advance: (id, to) =>
    f(`/api/rides/${id}/advance`, { method: 'POST', headers: H, body: JSON.stringify({ to }) }).then(j),
  cancel: (id) => f(`/api/rides/${id}/cancel`, { method: 'POST' }).then(j),
};

const H = { 'Content-Type': 'application/json' };
